import styled from '@emotion/styled'
import {
  ExtranetCustomerInvoiceSendingMethod,
  ExtranetShopProductDto,
  ExtranetShopPurchaseItemDto,
} from '@pharmac-extranet/extranet-api'
import {
  CheckIcon,
  DownArrowIcon,
  ExtranetContainer,
  ExtranetTable,
  ExtranetTableColumn,
  ExtranetTableRow,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import { PharmacButton, PharmacIcon, PharmacInput, PharmacText } from '@pharmac/ui'
import { useEffect } from 'react'
import { Controller } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useCustomerContext } from '../../../features/customer/CustomerProvider'
import { useUserCustomerDetails } from '../../../features/customer/useUserCustomerDetails'
import { useExtranetPath } from '../../../router/useExtranetPath'
import { api } from '../../../services/HttpService'
import { formatPrice } from '../../physical-products/util/price-utils'
import { useShop } from '../../shop-products/useShop'
import { useShoppingCartContext } from './ShoppingCartContext'
import { formatPercentage } from './utills/price-utils'

const VatColumns = styled(ExtranetTableColumn)`
  justify-content: flex-end;
  margin-right: var(--spacing-large);
`

type Props = {
  productsInCart: ExtranetShopPurchaseItemDto[]
  formhook: any
  productsInShoppingCart: any[]
  setProductList: (a: ExtranetShopProductDto[]) => void
  setOpenShoppingCart: (a: boolean) => void
}

const InfoText = styled(PharmacText)`
  padding-bottom: var(--spacing-tiny);
`

const ProductQuantityInput = styled(PharmacInput)`
  padding: 0 var(--spacing-tiny) 0 var(--spacing-tiny);
  margin: 0;
  justify-items: center;
  border: 0;
  &:hover {
    border: 0;
  }
  &:focus-within {
    outline: 0;
  }
`

const ProductTableColumn = styled(ExtranetTableColumn)`
  &:hover {
    color: var(--color-primary);
  }
`

export const ShoppingCartModalContent = ({
  productsInShoppingCart,
  formhook,
  setProductList,
  setOpenShoppingCart,
}: Props) => {
  const { t, i18n } = useLanguage()
  const { minDesktop } = useBreakpoint()
  const { customer } = useCustomerContext()
  const { userCustomer } = useUserCustomerDetails()
  const hook = useShoppingCartContext()
  const navigate = useNavigate()
  const createLink = useExtranetPath()
  const { langQuery } = useShop()

  const invoicingSendingMethod = (method: string | undefined) => {
    if (method === ExtranetCustomerInvoiceSendingMethod.Einvoice) return t('common:einvoice')
    if (method === ExtranetCustomerInvoiceSendingMethod.Email) return t('common:emailInvoice')
    if (method === ExtranetCustomerInvoiceSendingMethod.Print) return t('common:printInvoice')
  }

  const getProductInfo = async () =>
    await Promise.all(
      productsInShoppingCart.map(async (product) => {
        return await api.shop.getShopProductById(Number(customer?.id), Number(product.productId), {
          language: langQuery(i18n.language),
        })
      })
    )

  useEffect(() => {
    getProductInfo().then((a) => {
      setProductList(a)
    })
  }, [])

  const totalPriceWithoutVat = productsInShoppingCart.reduce(
    (accumulator, product) => accumulator + product.totalPriceWithoutVAT * product.quantity,
    0
  )

  const vatRows = new Map<number, number>()

  productsInShoppingCart.forEach((product) => {
    vatRows.set(
      product.taxPercentage,
      (vatRows.get(product.taxPercentage) ?? 0) + product.VAT * product.quantity
    )
  })

  const totalPriceWithVAT = productsInShoppingCart.reduce(
    (accumulator, product) => accumulator + product.totalPriceWithVAT * product.quantity,
    0
  )

  return (
    <>
      <ExtranetTable>
        {minDesktop && (
          <ExtranetTableRow
            style={{
              backgroundColor: 'var(--color-accent-3)',
              border: 'none',
              fontWeight: 600,
            }}
          >
            <ExtranetTableColumn
              size={minDesktop ? 5 : 3}
              marginSmallLeft
            >
              {t('common:name')}
            </ExtranetTableColumn>
            <ExtranetTableColumn size={3}>{t('common:quantity')}</ExtranetTableColumn>
            <ExtranetTableColumn
              size={1.5}
              flexJustify="center"
            >
              {t('extranet:physicalProducts.priceByPcs')}
            </ExtranetTableColumn>
            <ExtranetTableColumn size={1.5}>{t('shop.cart.totalPrice')}</ExtranetTableColumn>
            <ExtranetTableColumn
              size={1.5}
              flexJustify="center"
            >
              {t('extranet:physicalProducts.vat')} %
            </ExtranetTableColumn>
          </ExtranetTableRow>
        )}
        {!minDesktop && <div style={{ border: '1px solid var(--color-accent-4)' }} />}
        {productsInShoppingCart?.map((product) => (
          <>
            {minDesktop && (
              <ExtranetTableRow
                key={product.productId}
                style={{
                  backgroundColor: 'var(--color-accent-1)',
                  border: 'none',
                }}
              >
                <ProductTableColumn
                  size={minDesktop ? 5 : 3}
                  marginSmallLeft
                  marginSmallTop={!minDesktop}
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    navigate(createLink('product', { id: product.productId }))
                    setOpenShoppingCart(false)
                  }}
                >
                  <ExtranetContainer
                    flexRow
                    alignCenter
                  >
                    {product.hasroles ? (
                      <CheckIcon icon="checkCircle" />
                    ) : (
                      <DownArrowIcon icon="alertCircle" />
                    )}
                    <PharmacText
                      weight="semibold"
                      style={{ textDecoration: 'underline' }}
                    >
                      {product.title}
                    </PharmacText>
                  </ExtranetContainer>
                </ProductTableColumn>
                <>
                  <ExtranetTableColumn
                    size={3}
                    key={product.productId}
                  >
                    <ExtranetContainer
                      flexRow
                      alignCenter
                    >
                      <ExtranetContainer
                        flexRow
                        alignCenter
                        twoThirds
                      >
                        <ExtranetContainer
                          flexRow
                          alignCenter
                          style={{
                            borderRadius: '25px',
                            border: '1px solid var(--color-accent-4)',
                            width: '135px',
                          }}
                        >
                          <Controller
                            key={product.productId}
                            name={String(product.productId)}
                            control={formhook.control}
                            rules={{ min: 1, pattern: /^(0|[1-9]\d*)$/ }}
                            defaultValue={1}
                            render={({ field }) => (
                              <>
                                <ExtranetContainer
                                  justifyCenter
                                  paddingSmallHorizontal
                                  style={{
                                    cursor: 'pointer',
                                  }}
                                  onClick={() =>
                                    field.value >= 2 && field.onChange(String(--field.value))
                                  }
                                >
                                  <PharmacIcon icon="minus" />
                                </ExtranetContainer>
                                <ExtranetContainer
                                  style={{
                                    borderLeft: '1px solid var(--color-accent-4)',
                                    borderRight: '1px solid var(--color-accent-4)',
                                  }}
                                >
                                  <ProductQuantityInput
                                    {...field}
                                    value={field.value}
                                    onChange={(value) => {
                                      if (value === '' || /^[\d]{1,10}$/.test(value)) {
                                        return field.onChange(value)
                                      }
                                      return
                                    }}
                                  />
                                </ExtranetContainer>
                                <ExtranetContainer
                                  justifyCenter
                                  paddingSmallHorizontal
                                  style={{
                                    cursor: 'pointer',
                                  }}
                                  onClick={() => field.onChange(String(++field.value))}
                                >
                                  <PharmacIcon icon="plus" />
                                </ExtranetContainer>
                              </>
                            )}
                          />
                        </ExtranetContainer>
                      </ExtranetContainer>
                      <ExtranetContainer
                        third
                        justifyCenter
                      >
                        <PharmacButton
                          variant="secondary"
                          style={{ border: '1px solid var(--color-accent-4)' }}
                          marginMediumLeft
                          key={product.productId}
                          onClick={() => hook.addAndRemoveCartButton(product, product.productId)}
                        >
                          <PharmacIcon icon="trash2" />
                        </PharmacButton>
                      </ExtranetContainer>
                    </ExtranetContainer>
                  </ExtranetTableColumn>
                  <ExtranetTableColumn
                    size={1.5}
                    flexJustify="center"
                  >
                    {formatPrice(Number(product.unitSellingPrice))}
                  </ExtranetTableColumn>
                  <ExtranetTableColumn
                    size={1.5}
                    flexJustify="center"
                  >
                    {formatPrice(product.totalPriceWithoutVAT * product.quantity)}
                  </ExtranetTableColumn>
                  <ExtranetTableColumn
                    size={1.5}
                    flexJustify="center"
                  >
                    {formatPercentage(product.taxPercentage)}
                  </ExtranetTableColumn>
                </>
              </ExtranetTableRow>
            )}
            {!minDesktop && (
              <ExtranetContainer
                flexColumn
                style={{ border: '1px solid var(--color-accent-4)', borderRadius: '15px' }}
                marginSmallVertical
              >
                <ExtranetContainer
                  full
                  marginSmallBottom
                  flexColumn
                  key={product.productId}
                >
                  <ExtranetTableColumn
                    marginSmallLeft
                    marginSmallVertical
                    style={{
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      navigate(createLink('product', { id: product.productId }))
                      setOpenShoppingCart(false)
                    }}
                  >
                    <ExtranetContainer
                      flexRow
                      alignCenter
                      full
                    >
                      {product.hasroles ? (
                        <CheckIcon icon="checkCircle" />
                      ) : (
                        <DownArrowIcon icon="alertCircle" />
                      )}
                      <PharmacText
                        weight="semibold"
                        style={{ textDecoration: 'underline' }}
                      >
                        {product.title}
                      </PharmacText>
                    </ExtranetContainer>
                  </ExtranetTableColumn>
                  <div
                    style={{ borderBottom: '2px dashed var(--color-accent-4)', width: '100%' }}
                  />
                  <ExtranetContainer
                    marginMedium
                    flexRow
                    full
                  >
                    <ExtranetContainer
                      flexColumn
                      third
                    >
                      <PharmacText paddingSmallBottom>
                        {t('extranet:shop.orders.unitPrice')}
                      </PharmacText>
                      <PharmacText>{formatPrice(product.totalPriceWithoutVAT)}</PharmacText>
                    </ExtranetContainer>
                    <ExtranetContainer
                      flexColumn
                      third
                    >
                      <PharmacText paddingSmallBottom>
                        {t('extranet:shop.cart.totalPrice')}
                      </PharmacText>
                      <PharmacText weight="bold">
                        {formatPrice(product.totalPriceWithoutVAT * product.quantity)}
                      </PharmacText>
                    </ExtranetContainer>
                    <ExtranetContainer
                      flexColumn
                      third
                    >
                      <PharmacText paddingSmallBottom>
                        {t('extranet:physicalProducts.vat')} %
                      </PharmacText>
                      <PharmacText>{formatPercentage(product.taxPercentage)}</PharmacText>
                    </ExtranetContainer>
                  </ExtranetContainer>
                </ExtranetContainer>
                <ExtranetContainer
                  full
                  paddingSmallVertical
                  paddingSmallHorizontal
                  style={{
                    backgroundColor: 'var(--color-accent-3)',
                    borderRadius: '0 0 15px 15px',
                  }}
                >
                  <ExtranetContainer
                    flexRow
                    full
                    spaceBetween
                  >
                    <ExtranetContainer
                      full
                      flexRow
                      alignCenter
                    >
                      <ExtranetContainer
                        flexRow
                        marginSmallLeft
                        alignCenter
                        style={{ backgroundColor: 'var(--color-accent-1)', borderRadius: '25px' }}
                      >
                        <ExtranetContainer
                          flexRow
                          alignCenter
                          style={{
                            borderRadius: '25px',
                            border: '1px solid var(--color-accent-4)',
                            width: '135px',
                          }}
                        >
                          <Controller
                            key={product.productId}
                            name={String(product.productId)}
                            control={formhook.control}
                            rules={{ min: 1, pattern: /^(0|[1-9]\d*)$/ }}
                            defaultValue={1}
                            render={({ field }) => (
                              <>
                                <ExtranetContainer
                                  justifyCenter
                                  paddingSmallHorizontal
                                  onClick={() =>
                                    field.value >= 2 && field.onChange(String(--field.value))
                                  }
                                >
                                  <PharmacIcon icon="minus" />
                                </ExtranetContainer>
                                <ExtranetContainer
                                  style={{
                                    borderLeft: '1px solid var(--color-accent-4)',
                                    borderRight: '1px solid var(--color-accent-4)',
                                  }}
                                >
                                  <ProductQuantityInput
                                    {...field}
                                    value={field.value}
                                    onChange={(value) => {
                                      if (value === '' || /^[\d]{1,10}$/.test(value)) {
                                        return field.onChange(value)
                                      }

                                      return
                                    }}
                                  />
                                </ExtranetContainer>
                                <ExtranetContainer
                                  justifyCenter
                                  paddingSmallHorizontal
                                  onClick={() => field.onChange(String(++field.value))}
                                >
                                  <PharmacIcon icon="plus" />
                                </ExtranetContainer>
                              </>
                            )}
                          />
                        </ExtranetContainer>
                      </ExtranetContainer>
                    </ExtranetContainer>
                    <ExtranetContainer
                      justifyEnd
                      half
                      marginSmallRight
                    >
                      <PharmacButton
                        variant="secondary"
                        marginMediumLeft
                        style={{ border: '1px solid var(--color-accent-4)' }}
                        onClick={() => hook.addAndRemoveCartButton(product, product.productId)}
                      >
                        <PharmacIcon icon="trash2" />
                      </PharmacButton>
                    </ExtranetContainer>
                  </ExtranetContainer>
                </ExtranetContainer>
              </ExtranetContainer>
            )}
            {!product.hasroles && (
              <PharmacText
                paddingMediumLeft
                paddingMediumBottom
              >
                {t('extranet:shop.cart.missingRoles')}
              </PharmacText>
            )}
            <div
              style={{ borderBottom: minDesktop ? '1px solid var(--color-accent-4)' : undefined }}
            />
          </>
        ))}
      </ExtranetTable>
      <ExtranetContainer
        full
        justifyEnd
        flexRow
        paddingLargeVertical
      >
        <ExtranetTable>
          <ExtranetTableRow
            style={{
              backgroundColor: 'var(--color-accent-1)',
              padding: 0,
              margin: 0,
              border: 'none',
            }}
          >
            <ExtranetTableColumn
              shortHeight
              size={minDesktop ? 7 : 3}
            />
            <VatColumns
              shortHeight
              size={2}
            >
              {t('extranet:physicalProducts.total')} {t('extranet:physicalProducts.vat')} 0 %:
            </VatColumns>
            <ExtranetTableColumn
              shortHeight
              size={1.5}
            >
              {formatPrice(totalPriceWithoutVat)}
            </ExtranetTableColumn>
          </ExtranetTableRow>
          {[...vatRows].map((vatRow) => {
            return (
              <ExtranetTableRow
                style={{
                  backgroundColor: 'var(--color-accent-1)',
                  padding: 0,
                  margin: 0,
                  border: 'none',
                }}
              >
                <ExtranetTableColumn
                  shortHeight
                  size={minDesktop ? 7 : 3}
                />
                <VatColumns
                  shortHeight
                  size={2}
                >
                  {t('extranet:physicalProducts.vat')} {formatPercentage(vatRow[0])} %:
                </VatColumns>
                <ExtranetTableColumn
                  shortHeight
                  size={1.5}
                >
                  {formatPrice(vatRow[1])}
                </ExtranetTableColumn>
              </ExtranetTableRow>
            )
          })}
          <ExtranetTableRow
            style={{
              backgroundColor: 'var(--color-accent-1)',
              border: 'none',
              padding: 0,
              margin: 0,
              fontWeight: 600,
            }}
          >
            <ExtranetTableColumn
              shortHeight
              size={minDesktop ? 7 : 3}
            />
            <VatColumns
              shortHeight
              size={2}
            >
              {t('extranet:physicalProducts.total')}:
            </VatColumns>
            <ExtranetTableColumn
              shortHeight
              size={1.5}
            >
              {formatPrice(totalPriceWithVAT)}
            </ExtranetTableColumn>
          </ExtranetTableRow>
        </ExtranetTable>
      </ExtranetContainer>
      <ExtranetContainer
        full
        spaceBetween={minDesktop}
        paddingLarge={minDesktop}
        paddingLargeVertical={!minDesktop}
        paddingSmallLeft={!minDesktop}
        flexColumn={!minDesktop}
        style={{
          borderTop: '1px solid var(--color-accent-4)',
          borderBottom: '1px solid var(--color-accent-4)',
          fontWeight: 500,
        }}
      >
        <ExtranetContainer
          half={minDesktop}
          full={!minDesktop}
          flexColumn
        >
          <PharmacText
            weight="bold"
            paddingMediumBottom
          >
            {t('extranet:customer.deliveryInfo')}
          </PharmacText>
          <ExtranetContainer flexRow>
            <InfoText>
              {userCustomer?.billingAddressCard?.streetName}
              {','} {userCustomer?.billingAddressCard?.postCode}{' '}
              {userCustomer?.billingAddressCard?.townName}
            </InfoText>
          </ExtranetContainer>
          <InfoText>{userCustomer?.billingAddressCard?.emailAddress}</InfoText>
          <InfoText>{userCustomer?.billingAddressCard?.phoneNumber}</InfoText>
          <InfoText>{userCustomer?.billingAddressCard?.contactPersonName}</InfoText>
          <InfoText>{userCustomer?.officialName}</InfoText>
        </ExtranetContainer>
        <div style={{ height: '100%', borderLeft: '1px solid var(--color-accent-4)' }} />
        <ExtranetContainer
          half={minDesktop}
          paddingLargeLeft={minDesktop}
          paddingLargeTop={!minDesktop}
          full={!minDesktop}
          flexColumn
          style={minDesktop ? { borderLeft: '1px solid var(--color-accent-4)' } : undefined}
        >
          <PharmacText
            weight="bold"
            paddingMediumBottom
          >
            {t('extranet:customer.invoicingInfo')}
          </PharmacText>
          <InfoText>{invoicingSendingMethod(userCustomer?.invoiceSendingMethod)}</InfoText>
          <InfoText>{userCustomer?.department}</InfoText>
          <InfoText>{userCustomer?.invoiceRecipientOrganisationUnitNumber}</InfoText>
          <InfoText>{userCustomer?.invoiceToIntermediator}</InfoText>
        </ExtranetContainer>
      </ExtranetContainer>
    </>
  )
}
